import * as React from 'react';
import { CircularProgress, LinearProgress, Stack, Typography } from '@mui/material';
import theme from '../../themes/theme';
import { useTranslation } from 'react-i18next';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import {
    useContactIsDriverApi,
    useDeregisterAssetDriver,
    useGetLogbookDriversApi,
    useUpdateDriverRegistration,
    useUpdateLogbookPrivateJourney,
} from '../../api/LogbookApi';
import { useOutletContext } from 'react-router-dom';
import { useEffect } from 'react';
import SimpleSnackbar from '../ui/SimpleSnackbar';
import { useState } from 'react';
import { driverActionErrorMessage } from '../other/ConfigBasic';

/**
 * Return an empty String if the user is an invalid driver of this logbook
 * otherwise the contact person id from the user
 * @param {Object} allDriverData
 * @param {Array} driverIds
 * @param {Object} loginUser
 */
const checkUserIsLogbookDriver = (allDriverData, driverIds, loginUser) => {
    let logbookDriverData = allDriverData.filter((driver) => driverIds.includes(driver.id) && driver.loginUuid === loginUser.loginUuid);
    if (logbookDriverData.length === 0) {
        return '';
    } else {
        return logbookDriverData[0].id;
    }
};

export default function StatusLink(props) {
    const { t } = useTranslation();
    const [message, setMessage] = useState('');
    const currentLogbook = useOutletContext().logbookId;
    const driverIds = useGetLogbookDriversApi(currentLogbook);
    const allDriverData = useContactIsDriverApi(setMessage);
    const loginUser = useOutletContext().loginUser;
    let userDriverid = '';
    let registeredDriverId = '';
    const isPrivatModusActive = props.isPrivatModusActive;
    const { updateDriverRegistration } = useUpdateDriverRegistration();
    const { deregisterAssetDriver } = useDeregisterAssetDriver();
    const { updateLogbookPrivateJourney } = useUpdateLogbookPrivateJourney();
    const userAccess = useOutletContext().userAccess;

    // Whether an app-side registration exists at all. Distinct from "someone is
    // driving": a driver identified by driver key is the current driver but has no
    // registration to close, so logging out would be a no-op with a success message.
    const softwareRegistrationActive = props.currentDriver?.software?.active === true;

    // Closes the active registration on the asset. Reached from both logout branches.
    const runDeregister = (successKey, refetchQueries) => {
        if (!softwareRegistrationActive) {
            props.setMessage(t('noSoftwareDriverRegistration'));
            return;
        }

        deregisterAssetDriver({
            variables: { idAsset: props.idAsset },
            onCompleted: () => {
                props.setMessage(t(successKey));
            },
            onError: (error) => {
                props.setMessage(driverActionErrorMessage(error, t));
            },
            refetchQueries,
        });
    };

    useEffect(() => {
        if (props.connectionStatus > 0 && props.connectionStatus < 1) {
        } else if (
            userDriverid?.length > 0 &&
            registeredDriverId?.length > 0 &&
            userDriverid === registeredDriverId &&
            props?.connectionStatus !== 2 &&
            props?.connectionStatus !== 3
        ) {
            props?.setConnectionStatus(2);
        } else if (
            userDriverid.length > 0 &&
            registeredDriverId.length > 0 &&
            userDriverid !== registeredDriverId &&
            props?.connectionStatus !== 1 &&
            props?.connectionStatus !== 3
        ) {
            props?.setConnectionStatus(1);
        } else if (registeredDriverId?.length === 0 && props?.connectionStatus !== 0 && props?.connectionStatus !== 2) {
            props?.setConnectionStatus(0);
        }
    });

    if (!allDriverData || !driverIds) {
        return <LinearProgress />;
    }

    userDriverid = checkUserIsLogbookDriver(allDriverData, driverIds, loginUser);
    // The backend's answer, whatever the source. Wider than the old software-only
    // check: a driver identified by driver key now counts too, which is what makes
    // this agree with Maps. Null when nobody is driving, or when currentDriver was not
    // returned at all (older services/graph) — both fall back to "not registered".
    registeredDriverId = props.currentDriver?.driver?.id ?? '';

    const checkStatus = () => {
        if (userAccess < 2 && props.connectionStatus === 1) {
            props.setConnectionStatus(3);
        } else if (userDriverid.length === 0) {
            props.setMessage(t('notADriver'));
            props.setConnectionStatus(0);
        } else if (userDriverid.length > 0 && registeredDriverId.length === 0) {
            updateDriverRegistration({
                variables: { id: currentLogbook, register: true },
                onCompleted: () => {
                    props.setMessage(t('driverLoginInfo'));
                    props.setConnectionStatus(2);
                },
                onError: (error) => {
                    props.setMessage(driverActionErrorMessage(error, t));
                    props.setConnectionStatus(0);
                },
                refetchQueries: ['getAssetDataForStatus'],
            });
        } else if (userDriverid.length > 0 && registeredDriverId.length > 0 && userDriverid !== registeredDriverId) {
            props.setMessage(t('otherDriverRegistered'));
            props.setConnectionStatus(0);
        }
    };

    const handleConnection = () => {
        props.setConnectionStatus(0.5); // set the status to loading
        new Promise(
            (resolve) => setTimeout(() => resolve(), 500) // simulate a delay
        ).then(() => {
            checkStatus();
        });
    };

    return (
        <>
            {props.connectionStatus === 0 && (
                <Stack
                    direction='row'
                    justifyContent='center'
                    alignItems='center'
                    spacing={1}
                    sx={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        bgcolor: theme.palette.primary.light,
                        width: '226px',
                        height: '48px',
                        borderRadius: '12px',
                        textAlign: 'center',
                        cursor: 'pointer',
                    }}
                    onClick={handleConnection}
                >
                    <Typography sx={{ fontFamily: 'CircularProBold', textTransform: 'uppercase' }}>{t('registerAsDriver')}</Typography>
                    <LoginIcon
                        sx={{
                            height: '100%',
                            verticalAlign: 'middle',
                        }}
                    />
                </Stack>
            )}
            {props.connectionStatus > 0 && props.connectionStatus < 1 && (
                <Stack
                    direction='row'
                    justifyContent='center'
                    alignItems='center'
                    sx={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        bgcolor: theme.palette.primary.light,
                        width: '226px',
                        height: '48px',
                        borderRadius: '12px',
                        textAlign: 'center',
                        cursor: 'pointer',
                    }}
                >
                    <CircularProgress
                        size={30}
                        color='secondary'
                        sx={{
                            height: '100%',
                            verticalAlign: 'middle',
                        }}
                    />
                </Stack>
            )}
            {props.connectionStatus === 1 && (
                <Stack
                    direction='row'
                    justifyContent='center'
                    alignItems='center'
                    spacing={1}
                    sx={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        bgcolor: theme.palette.grey[300],
                        width: 'auto',
                        height: '48px',
                        borderRadius: '12px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        padding: '0 16px',
                        color: theme.palette.grey[600],
                    }}
                    onClick={handleConnection}
                >
                    <Typography sx={{ fontFamily: 'CircularProBold', textTransform: 'uppercase' }}>{t('otherDriverRegistered')}</Typography>
                    <LoginIcon
                        sx={{
                            height: '100%',
                            verticalAlign: 'middle',
                        }}
                    />
                </Stack>
            )}
            {props.connectionStatus === 2 && (
                <Stack
                    direction='row'
                    justifyContent='center'
                    alignItems='center'
                    spacing={1}
                    sx={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        bgcolor: theme.palette.primary.light,
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        textAlign: 'center',
                        cursor: 'pointer',
                    }}
                >
                    <LogoutIcon
                        sx={{
                            height: '100%',
                            verticalAlign: 'middle',
                        }}
                        onClick={() => props.setConnectionStatus(3)}
                    />
                </Stack>
            )}
            {props.connectionStatus === 3 && (
                <Stack
                    direction='row'
                    justifyContent='center'
                    alignItems='center'
                    spacing={1}
                    sx={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        bgcolor: '#FBE9E7',
                        width: '226px',
                        height: '48px',
                        borderRadius: '12px',
                        textAlign: 'center',
                        cursor: 'pointer',
                    }}
                    onClick={() => {
                        if (isPrivatModusActive) {
                            updateLogbookPrivateJourney({
                                variables: {
                                    idLogbook: currentLogbook,
                                    privateJourneyOn: !isPrivatModusActive,
                                },
                                onCompleted: () => {
                                    runDeregister('driverLogoutInfoExtension', [
                                        'getAssetDataForStatus',
                                        'getLogbookPrivateJourney',
                                    ]);
                                },
                                onError: (error) => {
                                    props.setMessage(driverActionErrorMessage(error, t));
                                },
                            });
                        } else {
                            runDeregister('driverLogoutInfo', ['getAssetDataForStatus']);
                        }
                    }}
                >
                    <Typography sx={{ fontFamily: 'CircularProBold', textTransform: 'uppercase' }}>{t('driverLogout')}</Typography>
                    <LogoutIcon
                        sx={{
                            height: '100%',
                            verticalAlign: 'middle',
                        }}
                    />
                </Stack>
            )}
            {message && <SimpleSnackbar message={message} setMessage={setMessage} />}
        </>
    );
}
